/**
 * OutputsView — the case outputs as cards (charts, PDFs, summaries) and its drafts as DraftCards.
 * Clicking a chart opens it in the viewer; clicking a PDF opens it in a new tab; an HTML output opens
 * in the viewer inside a sandboxed iframe (SandboxedHtml), never on the app origin. HTML and SVG links
 * download instead of opening (agentUi.fileLinkProps).
 * `openPath` (from wb.ui open_file) opens a chart, PDF or HTML output in the viewer or a draft
 * (outputs/drafts/<id>.json) in edit mode. A bare zoom event without a re-plotted chart falls back to
 * a CSS scale of the open chart with a "last 48 h" label.
 */

import { BarChart3, FileText, ListChecks, X } from 'lucide-react';
import DraftCard from './DraftCard.jsx';
import SyntheticBadge from './SyntheticBadge.jsx';
import SandboxedHtml from './SandboxedHtml.jsx';
import FileLink from './FileLink.jsx';
import { isImagePath, isPdfPath, isHtmlPath, draftIdFromPath, rangeLabel, fileLinkProps } from './agentUi.js';
import './WorkbenchDrawer.css';

const KIND_ICON = { chart: BarChart3, pdf: FileText, summary: ListChecks, csv: FileText, draft: FileText };

function OutputCard({ output, fileUrl, active, onOpen }) {
  const KindIcon = KIND_ICON[output.kind] ?? FileText;
  const url = fileUrl ? fileUrl(output.path) : null;
  const pdf = isPdfPath(output.path);
  return (
    <article className={`wbd__out${active ? ' is-open' : ''}`}>
      <button
        type="button"
        className="wbd__out-main"
        onClick={() => {
          // Images open in the viewer; PDFs open in a new tab (CONTRACT §10.4).
          if (pdf && url) window.open(url, '_blank', 'noopener,noreferrer');
          else onOpen?.(output.path, output.id);
        }}
        title={output.path}
      >
        {isImagePath(output.path) && url ? (
          <img className="wbd__thumb" src={url} alt="" loading="lazy" />
        ) : (
          <span className="wbd__out-icon"><KindIcon size={18} aria-hidden="true" /></span>
        )}
        <span className="wbd__out-title">{output.title}</span>
        {output.caption && <span className="wbd__out-caption">{output.caption}</span>}
      </button>
      {(output.facts || []).length > 0 && (
        <dl className="wbd__facts">
          {output.facts.map((f) => <div key={f.label}><dt>{f.label}</dt><dd>{f.value}</dd></div>)}
        </dl>
      )}
      <div className="wbd__out-foot">
        <SyntheticBadge />
        {pdf && url && <FileLink link={fileLinkProps(output.path, url)}>Open PDF</FileLink>}
      </div>
    </article>
  );
}

export default function OutputsView({
  outputs = [], drafts = [], highlight = {}, fileUrl = null, openPath = null, range = 'all',
  editDraftId = null, onOpen, onClose, onDraftOp, onDraftFocus, onEditDraft,
}) {
  const openDraftId = draftIdFromPath(openPath);
  const openDraft = openDraftId ? drafts.find((d) => d.id === openDraftId) ?? null : null;
  const viewerImage = openPath && isImagePath(openPath) ? openPath : null;
  const viewerPdf = openPath && isPdfPath(openPath) ? openPath : null;
  const viewerHtml = openPath && isHtmlPath(openPath) ? openPath : null;
  const zoomLabel = rangeLabel(range);
  const shownDrafts = drafts.filter((d) => d.status !== 'superseded' && d.id !== openDraft?.id);

  const draftOp = (d) => (op) => onDraftOp?.(d.id, d.version, op);

  return (
    <div className="wbd__outputs wbd__scroller">
      {(viewerImage || viewerPdf || viewerHtml || openDraft) && (
        <div className="wbd__viewer">
          <div className="wbd__viewer-head">
            <span className="wbd__fname">{openPath}</span>
            {viewerImage && zoomLabel && <span className="wbd__chip">{zoomLabel}</span>}
            {fileUrl && !openDraft && <FileLink link={fileLinkProps(openPath, fileUrl(openPath))} />}
            <button type="button" className="wbd__icon" onClick={() => onClose?.()} aria-label="Close viewer"><X size={12} aria-hidden="true" /></button>
          </div>
          {viewerImage && fileUrl && (
            <div className="wbd__zoomwrap">
              <img
                className={`wbd__viewimg${zoomLabel ? ' is-zoomed' : ''}`}
                src={fileUrl(viewerImage)}
                alt={openPath}
              />
            </div>
          )}
          {viewerPdf && fileUrl && <iframe className="wbd__pdf" src={fileUrl(viewerPdf)} title={viewerPdf} />}
          {viewerHtml && fileUrl && <SandboxedHtml url={fileUrl(viewerHtml)} title={viewerHtml} />}
          {openDraft && (
            <DraftCard
              draft={openDraft}
              highlight={highlight[openDraft.id] ?? null}
              editing={editDraftId === openDraft.id}
              onToggleEdit={() => onEditDraft?.(editDraftId === openDraft.id ? null : openDraft.id)}
              onOp={draftOp(openDraft)}
              onFocusLine={(lineId) => onDraftFocus?.(openDraft.id, lineId)}
              fileUrl={fileUrl}
            />
          )}
          {openDraftId && !openDraft && <div className="wbd__empty">That draft is not loaded yet.</div>}
        </div>
      )}

      {outputs.length === 0 && shownDrafts.length === 0 && !openDraft && (
        <div className="wbd__empty">No outputs yet.</div>
      )}

      {outputs.length > 0 && (
        <div className="wbd__grid">
          {outputs.filter((o) => !o.draft_id).map((o) => (
            <OutputCard key={o.id} output={o} fileUrl={fileUrl} active={o.path === openPath} onOpen={onOpen} />
          ))}
        </div>
      )}

      {shownDrafts.length > 0 && (
        <div className="wbd__drafts">
          {shownDrafts.map((d) => (
            <div
              key={d.id}
              className="wbd__draftwrap"
              onClickCapture={() => onDraftFocus?.(d.id, null)}
            >
              <DraftCard
                draft={d}
                compact
                highlight={highlight[d.id] ?? null}
                editing={editDraftId === d.id}
                onToggleEdit={() => {
                  onEditDraft?.(editDraftId === d.id ? null : d.id);
                  onOpen?.(`outputs/drafts/${d.id}.json`);
                }}
                onOp={draftOp(d)}
                onFocusLine={(lineId) => onDraftFocus?.(d.id, lineId)}
                fileUrl={fileUrl}
              />
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
